import { Injectable } from '@angular/core';

import { TagLocation, TagNode } from '@mhe/reader/models';
import { TopicTocReverseMap } from './topic-toc-reverse-map';

import { TopicResolutionHints, resolveMatchedTopicNode } from '../topics-metadata.resolver';

interface TopicMetadataContext {
  topicsTree: unknown
  reverseMap: TopicTocReverseMap | undefined
  selectedLocation: TagLocation | null
  hints?: TopicResolutionHints
}

export interface TopicMetadataSnapshot {
  topicNode: TagNode | null
  lastEvaluatedSpineId: string | null
  lastEvaluatedCfi: string | null
  lastUpdatedAt: number
}

@Injectable({
  providedIn: 'root',
})
export class TopicMetadataStore {
  private currentSnapshot: TopicMetadataSnapshot = {
    topicNode: null,
    lastEvaluatedSpineId: null,
    lastEvaluatedCfi: null,
    lastUpdatedAt: 0,
  };

  private lastContext: TopicMetadataContext | null = null;

  updateFromNavigationContext(
    topicsTree: unknown,
    reverseMap: TopicTocReverseMap | undefined,
    selectedLocation: TagLocation | null,
    currentSpineId: string,
    hints?: TopicResolutionHints,
  ): TagNode | null {
    const topicNode = resolveMatchedTopicNode(
      topicsTree,
      selectedLocation,
      currentSpineId,
      hints,
      reverseMap,
    );

    this.lastContext = {
      topicsTree,
      reverseMap,
      selectedLocation,
      hints,
    };

    this.currentSnapshot = {
      topicNode,
      lastEvaluatedSpineId: currentSpineId || null,
      lastEvaluatedCfi: selectedLocation?.cfi || hints?.targetCfi || null,
      lastUpdatedAt: Date.now(),
    };

    console.log('[TopicMetadataStore] matchedTopicNode', this.currentSnapshot);

    return topicNode;
  }

  getCurrentMetadata(): TagNode | null {
    return this.currentSnapshot.topicNode;
  }

  getCurrentSnapshot(): TopicMetadataSnapshot {
    return this.currentSnapshot;
  }

  resolveForLocation(currentSpineId: string, currentCfi: string): TopicMetadataSnapshot {
    const isExactFresh =
      this.currentSnapshot.lastEvaluatedSpineId === currentSpineId &&
      this.currentSnapshot.lastEvaluatedCfi === currentCfi;

    const isSpineFreshWithTopic =
      this.currentSnapshot.lastEvaluatedSpineId === currentSpineId &&
      Boolean(this.currentSnapshot.topicNode);

    if (isExactFresh || isSpineFreshWithTopic) {
      return this.currentSnapshot;
    }

    if (!this.lastContext) {
      this.currentSnapshot = {
        topicNode: null,
        lastEvaluatedSpineId: currentSpineId || null,
        lastEvaluatedCfi: currentCfi || null,
        lastUpdatedAt: Date.now(),
      };

      return this.currentSnapshot;
    }

    const refreshedHints: TopicResolutionHints = {
      ...(this.lastContext.hints || {}),
      targetSpineId: currentSpineId,
    };

    const selectedLocationForSpine =
      this.lastContext.selectedLocation?.spineId === currentSpineId
        ? this.lastContext.selectedLocation
        : null;

    const topicNode = resolveMatchedTopicNode(
      this.lastContext.topicsTree,
      selectedLocationForSpine,
      currentSpineId,
      refreshedHints,
      this.lastContext.reverseMap,
    );

    // Do not overwrite a known good topic on the same spine with a null recompute result.
    const shouldKeepPreviousTopic =
      !topicNode &&
      this.currentSnapshot.lastEvaluatedSpineId === currentSpineId &&
      Boolean(this.currentSnapshot.topicNode);

    this.currentSnapshot = {
      topicNode: shouldKeepPreviousTopic ? this.currentSnapshot.topicNode : topicNode,
      lastEvaluatedSpineId: currentSpineId || null,
      lastEvaluatedCfi: currentCfi || null,
      lastUpdatedAt: Date.now(),
    };

    return this.currentSnapshot;
  }
}
export type { TopicResolutionHints };
